import { useEffect, useMemo, useRef, useState } from 'react';
import Spinner from '../common/Spinner';
import customOrdersService from '../../services/customorders.service';
import PrintContainer from '../PrintContainer';
import ThermalReceipt, { ThermalReceiptRow, ThermalReceiptSection } from '../receipts/ThermalReceipt';
import { getStoreLabel } from '../../constants/stores';
import storeLogo from '../../images/logo-los-portales.png';

const emptyItem = { name: '', quantity: 1, price: '' };

export default function CustomOrderForm({ storeId }) {
    const [department, setDepartment] = useState('');
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [pickupDate, setPickupDate] = useState('');
    const [pickupTime, setPickupTime] = useState('');
    const [items, setItems] = useState([{ ...emptyItem }]);
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [submittedOrder, setSubmittedOrder] = useState(null);
    const receiptRef = useRef();
    const [paid, setPaid] = useState(false);
    const timeSlots = useMemo(() => {
        const slots = [];
        const startMinutes = 8 * 60;
        const endMinutes = 21 * 60; // 9pm inclusive
        for (let minutes = startMinutes; minutes <= endMinutes; minutes += 30) {
            const hour24 = Math.floor(minutes / 60);
            const mins = (minutes % 60).toString().padStart(2, '0');
            const period = hour24 >= 12 ? 'PM' : 'AM';
            const hour12Raw = hour24 % 12 === 0 ? 12 : hour24 % 12;
            const display = `${hour12Raw}:${mins} ${period}`;
            const value = `${hour24.toString().padStart(2, '0')}:${mins}`;
            slots.push({ value, display });
        }
        return slots;
    }, []);

    const todayStr = new Date().toISOString().split('T')[0];
    const isPickupToday = pickupDate === todayStr;

    // When the pickup date is today, hide time slots that have already passed.
    const availableTimeSlots = useMemo(() => {
        if (!isPickupToday) return timeSlots;
        const now = new Date();
        const nowMinutes = now.getHours() * 60 + now.getMinutes();
        return timeSlots.filter((slot) => {
            const [h, m] = slot.value.split(':').map(Number);
            return h * 60 + m > nowMinutes;
        });
    }, [isPickupToday, timeSlots]);

    // Clear the selected time if it is no longer bookable
    // (e.g. cashier switched the date to today and the hour passed).
    useEffect(() => {
        if (pickupTime && !availableTimeSlots.some((slot) => slot.value === pickupTime)) {
            setPickupTime('');
        }
    }, [availableTimeSlots, pickupTime]);

    function handleItemChange(index, field, value) {
        const updated = [...items];
        updated[index] = {
            ...updated[index],
            [field]: field === 'quantity' && value ? Number(value) : value
        };
        setItems(updated);
    }

    function addItemRow() {
        setItems([...items, { ...emptyItem }]);
    }

    function removeItemRow(index) {
        if (items.length === 1) return;
        setItems(items.filter((_, idx) => idx !== index));
    }

    function resetForm() {
        setDepartment('');
        setCustomerName('');
        setCustomerPhone('');
        setPickupDate('');
        setPickupTime('');
        setItems([{ ...emptyItem }]);
        setPaid(false);
    }

    function validate() {
        if (!department || !customerName || !customerPhone || !pickupDate || !pickupTime) {
            alert('Por favor llena todos los campos requeridos.');
            return false;
        }
        if (!storeId) {
            alert('Tu perfil no tiene una tienda asignada. Contacta a un administrador.');
            return false;
        }
        const hasValidItems = items.every((item) => item.name && item.quantity);
        if (!hasValidItems) {
            alert('Cada articulo necesita un nombre y cantidad.');
            return false;
        }
        return true;
    }

    async function handleSubmit(e) {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);
        setSuccessMessage('');
        try {
            const pickupUtc = new Date(`${pickupDate}T${pickupTime}:00`);
            const newOrder = await customOrdersService.createCustomOrder({
                department,
                storeId,
                customerName,
                customerPhone,
                pickupDate,
                pickupTime,
                paid,
                pickupDateTimeUtc: pickupUtc.toISOString(),
                items: items.map((item) => ({
                    name: item.name,
                    quantity: Number(item.quantity),
                    price:
                        item.price === '' || item.price === null || item.price === undefined
                            ? null
                            : Number(item.price)
                }))
            });
            setSuccessMessage('Orden guardada.');
            setSubmittedOrder(newOrder);
            resetForm();
        } catch (error) {
            console.error('Error creating custom order', error);
            alert('No se pudo guardar la orden.');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="custom-orders-form">
            <div className="custom-form-header">
                <div>
                    <h2>Nueva Orden</h2>
                    <p className="subtitle">Breve y claro para el equipo.</p>
                </div>
                {successMessage && <p className="successMessage">{successMessage}</p>}
            </div>
            <form onSubmit={handleSubmit}>
                {!storeId && (
                    <div className="custom-card-section" style={{ border: '1px solid #f4c7b2', color: '#8d1f0c' }}>
                        <p style={{ margin: 0 }}>
                            No tienes una tienda asignada en tu perfil. Contacta a gerencia para continuar.
                        </p>
                    </div>
                )}
                <div className="custom-card-section">
                    <h3>Cliente</h3>
                    <div className="custom-section-grid">
                        <div className="formEntry">
                            <label>Nombre</label>
                            <input
                                type="text"
                                value={customerName}
                                onChange={(e) => setCustomerName(e.target.value)}
                            />
                        </div>
                        <div className="formEntry">
                            <label>Telefono</label>
                            <input
                                type="text"
                                value={customerPhone}
                                onChange={(e) => setCustomerPhone(e.target.value)}
                                placeholder="+19105550123"
                            />
                        </div>
                        <div className="formEntry">
                            <label>Departamento</label>
                            <input
                                type="text"
                                value={department}
                                onChange={(e) => setDepartment(e.target.value)}
                                placeholder="ej. Deli"
                            />
                        </div>
                        <div className="formEntry">
                            <label>Tienda</label>
                            <div className="readonlyField">
                                {storeId ? getStoreLabel(storeId) : 'Sin asignar'}
                            </div>
                        </div>
                        <div className="formEntry">
                            <label>Pagado?</label>
                            <select
                                value={paid ? 'yes' : 'no'}
                                onChange={(e) => setPaid(e.target.value === 'yes')}
                            >
                                <option value="no">No</option>
                                <option value="yes">Si</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="custom-card-section">
                    <h3>Recogida</h3>
                    <div className="custom-section-grid custom-section-grid--pickup">
                        <div className="formEntry">
                            <label>Fecha</label>
                            <input
                                type="date"
                                value={pickupDate}
                                min={todayStr}
                                onChange={(e) => setPickupDate(e.target.value)}
                            />
                        </div>
                        <div className="formEntry">
                            <label>Hora</label>
                            {availableTimeSlots.length === 0 ? (
                                <p className="form-hint">Ya no hay horarios disponibles hoy. Elige otra fecha.</p>
                            ) : (
                                <div className="time-select">
                                    <select value={pickupTime} onChange={(e) => setPickupTime(e.target.value)}>
                                        <option value="">Seleccionar...</option>
                                        {availableTimeSlots.map((slot) => (
                                            <option value={slot.value} key={slot.value}>
                                                {slot.display}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="custom-card-section">
                    <div className="custom-card-header">
                        <h3>Articulos</h3>
                        <p className="section-helper">Nombre, cantidad, precio (opcional).</p>
                    </div>
                    <div className="custom-items-grid">
                        {items.map((item, index) => (
                            <div className="custom-item-row" key={`item-${index}`}>
                                <input
                                    type="text"
                                    placeholder="Articulo"
                                    value={item.name}
                                    onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                                />
                                <input
                                    type="number"
                                    placeholder="Cant."
                                    min="1"
                                    value={item.quantity}
                                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                />
                                <input
                                    type="number"
                                    placeholder="Precio"
                                    min="0"
                                    step="0.01"
                                    value={item.price ?? ''}
                                    onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                                />
                                <button
                                    type="button"
                                    onClick={() => removeItemRow(index)}
                                    className="lightButton"
                                    disabled={items.length === 1}
                                >
                                    Eliminar
                                </button>
                            </div>
                        ))}
                        <button type="button" onClick={addItemRow} className="secondaryButton">
                            + Agregar articulo
                        </button>
                    </div>
                </div>

                <div className="custom-form-actions">
                    {loading ? (
                        <Spinner />
                    ) : (
                        <button type="submit" className="primaryButton" disabled={!storeId}>
                            Guardar Orden
                        </button>
                    )}
                </div>
            </form>
            {submittedOrder && (
                <div className="custom-card-section">
                    <h3>Recibo</h3>
                    <PrintContainer
                        contentRef={receiptRef}
                        receiptData={{
                            title: 'Orden Especial',
                            subtitle: getStoreLabel(submittedOrder.storeId),
                            meta: [
                                { label: 'Recogida', value: `${submittedOrder.pickupDate} ${submittedOrder.pickupTime}` },
                                { label: 'Pagado', value: submittedOrder.paid ? 'SI' : 'NO' },
                            ],
                            sections: [
                                {
                                    title: 'Cliente',
                                    rows: [
                                        { label: 'Nombre', value: submittedOrder.customerName },
                                        { label: 'Telefono', value: submittedOrder.customerPhone },
                                        { label: 'Departamento', value: submittedOrder.department || 'N/A' },
                                    ],
                                },
                                {
                                    title: 'Articulos',
                                    items: (submittedOrder.items || []).map(item => ({
                                        text: `${item.quantity} x ${item.name}`,
                                        price: item.price != null && item.price !== '' ? `$${Number(item.price).toFixed(2)}` : undefined,
                                    })),
                                },
                            ],
                            footerNote: 'Gracias por tu orden',
                        }}
                    >
                        <ThermalReceipt
                            logoSrc={storeLogo}
                            logoAlt={`${getStoreLabel(submittedOrder.storeId)} logo`}
                            title="Orden Especial"
                            subtitle={getStoreLabel(submittedOrder.storeId)}
                            meta={[
                                { label: 'Recogida', value: `${submittedOrder.pickupDate} ${submittedOrder.pickupTime}` },
                                { label: 'Pagado', value: submittedOrder.paid ? 'SI' : 'NO' }
                            ]}
                            footerNote="Gracias por tu orden"
                        >
                            <ThermalReceiptSection title="Cliente">
                                <ThermalReceiptRow label="Nombre" value={submittedOrder.customerName} />
                                <ThermalReceiptRow label="Telefono" value={submittedOrder.customerPhone} />
                                <ThermalReceiptRow label="Departamento" value={submittedOrder.department || 'N/A'} />
                            </ThermalReceiptSection>
                            <ThermalReceiptSection title="Articulos" showSeparator={false}>
                                <ul className="thermal-receipt__list">
                                    {submittedOrder.items?.map((item, index) => (
                                        <li className="thermal-receipt__list-item" key={`${submittedOrder.id}-receipt-${index}`}>
                                            <span>{item.quantity} x {item.name}</span>
                                            <span>
                                                {item.price === null || item.price === undefined || item.price === ''
                                                    ? ''
                                                    : `$${Number(item.price).toFixed(2)}`
                                                }
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </ThermalReceiptSection>
                        </ThermalReceipt>
                    </PrintContainer>
                    <button type="button" className="lightButton" onClick={() => setSubmittedOrder(null)}>
                        Listo
                    </button>
                </div>
            )}
        </div>
    );
}
